"use client";

import Image from "next/image";
import React, { useState, useRef, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  Calculator,
  Zap,
  CheckCircle2,
  Mail,
  Link as LinkIcon,
  RefreshCw,
  Download,
  X,
  BookOpen,
  Scale,
  Info,
  Sparkles,
} from "lucide-react";

// Ensure GSAP plugin is registered
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

// --- BYE-LAW EDUCATION MODAL ---
const BreakdownModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const bylawItems = [
    {
      icon: Scale,
      title: "Sinking Fund (Bye-law 66)",
      rule: "Minimum 0.25% of Construction Cost",
      explanation:
        "This fund is reserved for major structural repairs and reconstruction. It's a legal mandate under Maharashtra Co-operative Societies Act to ensure long-term building stability.",
      color: "orange",
    },
    {
      icon: Scale,
      title: "Repair Fund (Bye-law 67)",
      rule: "Minimum 0.75% of Construction Cost",
      explanation:
        "Used for regular maintenance, painting, plumbing, and electrical repairs. Calculated based on your flat's construction cost as per conveyance deed.",
      color: "blue",
    },
    {
      icon: BookOpen,
      title: "Service Charges (Bye-law 13)",
      rule: "Equal Share per Member",
      explanation:
        "Common expenses like security, lift maintenance, cleaning, and admin costs are divided equally among all members, regardless of flat size.",
      color: "green",
    },
    {
      icon: Info,
      title: "Non-Occupancy (Bye-law 42)",
      rule: "Max 10% of Service Charges",
      explanation:
        "If your flat is rented out, a nominal charge applies to cover additional wear and tear on common facilities. Capped at 10% by law.",
      color: "purple",
    },
    {
      icon: BookOpen,
      title: "Property Tax (Form N)",
      rule: "Actuals as per BMC Bill",
      explanation:
        "Recovered as actual amounts paid to the municipal corporation. Society collects on behalf and remits directly.",
      color: "red",
    },
    {
      icon: Info,
      title: "Water Charges",
      rule: "Per Inlet or Actuals",
      explanation:
        "Based on number of water inlets or sub-metered readings. Ensures fair distribution based on actual usage.",
      color: "cyan",
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-xl"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 50 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="relative bg-[#111] border border-white/10 rounded-3xl p-6 md:p-8 max-w-3xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/20 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-widest mb-4">
                <Sparkles className="w-3 h-3" /> Maharashtra Bye-laws Explained
              </div>
              <h2 className="text-2xl md:text-3xl font-clash font-bold text-white mb-2">
                Why Your Bill Looks Like This
              </h2>
              <p className="text-gray-400 text-sm max-w-lg mx-auto">
                Every line item on your maintenance bill is governed by the
                Maharashtra Co-operative Societies Act and Model Bye-laws.
                Here&apos;s what each charge means.
              </p>
            </div>

            {/* Bye-law Cards */}
            <div className="grid md:grid-cols-2 gap-4">
              {bylawItems.map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/[0.07] transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                        item.color === "orange"
                          ? "bg-orange-500/10 text-orange-500"
                          : item.color === "blue"
                            ? "bg-blue-500/10 text-blue-500"
                            : item.color === "green"
                              ? "bg-green-500/10 text-green-500"
                              : item.color === "purple"
                                ? "bg-purple-500/10 text-purple-500"
                                : item.color === "red"
                                  ? "bg-red-500/10 text-red-500"
                                  : "bg-cyan-500/10 text-cyan-500"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium font-clash text-sm mb-1">
                        {item.title}
                      </h4>
                      <p className="text-orange-400 text-[10px] font-medium uppercase tracking-wider mb-2">
                        {item.rule}
                      </p>
                      <p className="text-gray-400 text-xs leading-relaxed">
                        {item.explanation}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-gray-500 text-xs mb-4">
                SmartDwara automates all these calculations so your society
                stays 100% compliant.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white font-clash font-semibold tracking-wide text-sm rounded-full transition-colors"
              >
                Got it, Thanks!
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// --- MAIN COMPONENT ---
const BillingSection = () => {
  const containerRef = useRef(null);
  const receiptRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // --- STATE FOR CALCULATOR ---
  const [constructionCost, setConstructionCost] = useState(1400000); // Per sq.ft cost * area
  const [carpetArea, _setCarpetArea] = useState(1200);
  const [serviceCharge, setServiceCharge] = useState(1500);
  const [cars, setCars] = useState(1);
  const [bikes, setBikes] = useState(1);
  const [isRented, setIsRented] = useState(false);
  const [waterInlets, setWaterInlets] = useState(2);
  const [propertyTax, setPropertyTax] = useState(1200);

  const [_isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // --- FORM N COMPLIANT CALCULATIONS ---
  const billDetails = useMemo(() => {
    // Bye-law 66: Sinking Fund = 0.25% of Construction Cost
    const sinkingFund = constructionCost * 0.0025;

    // Bye-law 67: Repair Fund = 0.75% of Construction Cost
    const repairFund = constructionCost * 0.0075;

    // Service Charges: Equal per flat (Fixed)
    const serviceCharges = serviceCharge;

    // Parking: Fixed per slot
    const parkingCharges = cars * 500 + bikes * 200;

    // Water: Per inlet
    const waterCharges = waterInlets * 225;

    // Non-Occupancy: Max 10% of Service Charges (Bye-law 42)
    const nonOccupancy = isRented ? serviceCharge * 0.1 : 0;

    // Property Tax: Actuals
    const propTax = propertyTax;

    const totalPayable =
      propTax +
      waterCharges +
      sinkingFund +
      repairFund +
      serviceCharges +
      parkingCharges +
      nonOccupancy;

    return {
      propertyTax: propTax,
      waterCharges,
      sinkingFund,
      repairFund,
      serviceCharges,
      parkingCharges,
      nonOccupancy,
      totalPayable,
    };
  }, [
    constructionCost,
    serviceCharge,
    cars,
    bikes,
    isRented,
    waterInlets,
    propertyTax,
  ]);

  // --- ANIMATIONS ---
  useGSAP(
    () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: containerRef.current,
          start: "top 75%",
          toggleActions: "play none none reverse",
        },
      });

      tl.from(".billing-header", {
        y: 40,
        opacity: 0,
        duration: 0.8,
        ease: "power3.out",
      })
        .from(
          ".calculator-ui",
          { x: -50, opacity: 0, duration: 0.8, ease: "power2.out" },
          "-=0.4",
        )
        .from(
          receiptRef.current,
          { x: 50, opacity: 0, duration: 0.8, ease: "power2.out" },
          "-=0.6",
        );

      gsap.from(".workflow-card", {
        y: 30,
        opacity: 0,
        duration: 0.6,
        stagger: 0.15,
        scrollTrigger: {
          trigger: ".workflow-grid",
          start: "top 85%",
        },
      });
    },
    { scope: containerRef },
  );

  return (
    <section
      id="billing"
      ref={containerRef}
      className="py-16 md:py-24 bg-[#050505] px-4 relative overflow-hidden border-t border-white/5"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-200 h-125 bg-orange-500/5 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* HEADER */}
        <div className="billing-header text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-500/20 bg-orange-500/10 text-orange-400 text-[10px] font-bold uppercase tracking-widest mb-4">
            <Calculator className="w-3 h-3" /> Form N • Bye-law 66-67 Compliant
          </div>
          <h2 className="text-5xl md:text-6xl font-clash font-bold tracking-normal text-white mb-4 leading-tight">
            Legally Accurate. <br />
            <span className="text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
              Auto-Generated Bills.
            </span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            Sinking Fund (0.25%). Repair Fund (0.75%). Non-Occupancy (max 10%).
            <br className="hidden md:block" />
            Every line item follows Maharashtra Co-operative Societies Bye-laws.
          </p>
        </div>

        {/* --- SPLIT LAYOUT --- */}
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-start mb-24">
          {/* LEFT: CALCULATOR UI */}
          <div className="calculator-ui lg:col-span-5 bg-[#111] border border-white/10 rounded-4xl p-6 md:p-8 shadow-2xl h-fit sticky top-24">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-lg md:text-xl font-clash font-semibold tracking-wide text-white flex items-center gap-2">
                <Zap className="w-5 h-5 text-orange-500" /> Bill Simulator
              </h3>
              <span className="text-[10px] md:text-xs text-gray-500 font-mono uppercase tracking-widest">
                Form N Demo
              </span>
            </div>

            <div className="space-y-5">
              {/* Construction Cost */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center justify-between">
                  <span>Construction Cost (for SF/RF)</span>
                  <span className="text-orange-500 font-mono">
                    ₹{constructionCost.toLocaleString("en-IN")}
                  </span>
                </label>
                <input
                  type="range"
                  min="500000"
                  max="5000000"
                  step="50000"
                  value={constructionCost}
                  onChange={(e) => setConstructionCost(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                />
                <div className="flex justify-between text-[10px] text-gray-600 font-mono">
                  <span>₹5L</span>
                  <span>₹50L</span>
                </div>
              </div>

              {/* Property Tax */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Property Tax (Actuals)
                </label>
                <div className="relative group">
                  <input
                    type="number"
                    value={propertyTax}
                    onChange={(e) => setPropertyTax(Number(e.target.value))}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-base md:text-sm text-white focus:border-orange-500 focus:outline-none transition-colors font-mono"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 text-xs font-bold pointer-events-none">
                    INR
                  </div>
                </div>
              </div>

              {/* Service Charge */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Service Charge (Equal Share)
                </label>
                <div className="relative group">
                  <input
                    type="number"
                    value={serviceCharge}
                    onChange={(e) => setServiceCharge(Number(e.target.value))}
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-xl px-4 py-3 text-base md:text-sm text-white focus:border-orange-500 focus:outline-none transition-colors font-mono"
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-600 text-xs font-bold pointer-events-none">
                    INR
                  </div>
                </div>
              </div>

              {/* Water Inlets */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider flex items-center justify-between">
                  <span>Water Inlets</span>
                  <span className="text-orange-500">{waterInlets}</span>
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  value={waterInlets}
                  onChange={(e) => setWaterInlets(Number(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
                />
                <div className="flex justify-between text-[10px] text-gray-500 font-mono">
                  <span>1</span>
                  <span>5</span>
                </div>
              </div>

              {/* Parking */}
              <div className="space-y-2">
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                  Parking Slots
                </label>
                <div className="flex gap-2">
                  <div className="flex-1 flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-3">
                    <span className="text-gray-400 text-xs">Car (₹500)</span>
                    <input
                      type="number"
                      min="0"
                      max="3"
                      value={cars}
                      onChange={(e) => setCars(Number(e.target.value))}
                      className="w-12 bg-transparent text-right text-base md:text-sm text-white font-mono focus:outline-none"
                    />
                  </div>
                  <div className="flex-1 flex items-center justify-between bg-[#0A0A0A] border border-white/10 rounded-xl px-3 py-3">
                    <span className="text-gray-400 text-xs">Bike (₹200)</span>
                    <input
                      type="number"
                      min="0"
                      max="5"
                      value={bikes}
                      onChange={(e) => setBikes(Number(e.target.value))}
                      className="w-12 bg-transparent text-right text-base md:text-sm text-white font-mono focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Toggle Rented */}
              <div className="p-4 bg-[#0A0A0A] rounded-xl border border-white/5 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-white">
                    Flat Rented?
                  </div>
                  <div className="text-xs text-gray-500">
                    +10% Non-Occupancy (Bye-law 42)
                  </div>
                </div>
                <button
                  onClick={() => setIsRented(!isRented)}
                  className={`w-12 h-6 rounded-full p-1 transition-colors ${
                    isRented ? "bg-orange-500" : "bg-gray-700"
                  }`}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full transition-transform ${
                      isRented ? "translate-x-6" : "translate-x-0"
                    }`}
                  />
                </button>
              </div>

              {/* Compliance Badges */}
              <div className="pt-5 border-t border-white/10">
                <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mb-3">
                  Bye-law Compliance
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />{" "}
                    Form N Format
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />{" "}
                    Bye-law 66
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />{" "}
                    Bye-law 67
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400">
                    <CheckCircle2 className="w-3 h-3 text-green-500 shrink-0" />{" "}
                    Bye-law 42
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT: FORM N INVOICE (FLOATING) */}
          <div className="lg:col-span-7 flex justify-center perspective-[1000px] w-full">
            <motion.div
              ref={receiptRef}
              animate={{ y: [0, -12, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="bg-white text-black w-full max-w-105 md:max-w-120 p-5 md:p-8 rounded-2xl shadow-[0_25px_60px_rgba(0,0,0,0.6)] relative font-sans text-sm border border-gray-200"
            >
              {/* FORM N Header */}
              <header className="border-b-2 border-gray-800 pb-4 mb-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center border border-orange-300 text-orange-600 font-bold text-lg shrink-0">
                      GD
                    </div>
                    <div>
                      <span className="text-base md:text-lg font-bold text-gray-900 leading-tight block">
                        Gokuldham Co-op Hsg. Soc. Ltd.
                      </span>
                      <p className="text-[9px] text-gray-500 font-semibold uppercase tracking-wide">
                        Reg. No: MUM/HSG/TC/1234/2010
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex justify-between items-end">
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">
                      Maintenance Bill (Form N)
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      Bill No:{" "}
                      <span className="font-bold text-black">
                        #MNT-2026-02-001
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-gray-500">
                      Date: <span className="font-semibold">01 Feb 2026</span>
                    </p>
                    <p className="text-[10px] text-red-600 font-bold">
                      Due: 15 Feb 2026
                    </p>
                  </div>
                </div>
              </header>

              {/* Bill To */}
              <div className="mb-5 p-4 bg-gray-50 rounded-xl border border-gray-100">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Member
                    </p>
                    <h3 className="text-base font-bold text-gray-900">
                      Mr. Atmaram Bhide
                    </h3>
                    <p className="text-[11px] text-gray-600">
                      Unit: <span className="font-bold">A-101</span> • Wing A
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Area: {carpetArea} Sq.Ft • Residential
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Amount Due
                    </p>
                    <h3 className="text-xl md:text-2xl font-black text-gray-900">
                      ₹{billDetails.totalPayable.toLocaleString("en-IN")}
                    </h3>
                  </div>
                </div>
              </div>

              {/* FORM N TABLE */}
              <div className="w-full overflow-x-auto mb-5">
                <table className="w-full text-left border-collapse text-[11px]">
                  <thead>
                    <tr className="border-b border-gray-300 bg-gray-100">
                      <th className="py-2 px-2 text-[9px] uppercase text-gray-500 font-bold">
                        Particulars
                      </th>
                      <th className="py-2 px-2 text-[9px] uppercase text-gray-500 font-bold text-right">
                        Basis
                      </th>
                      <th className="py-2 px-2 text-[9px] uppercase text-gray-500 font-bold text-right">
                        Amount (₹)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-800">
                    <tr className="border-b border-gray-100">
                      <td className="py-2.5 px-2 font-medium">Property Tax</td>
                      <td className="py-2.5 px-2 text-right text-gray-500 text-[10px]">
                        Actuals
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold">
                        {billDetails.propertyTax.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2.5 px-2 font-medium">Water Charges</td>
                      <td className="py-2.5 px-2 text-right text-gray-500 text-[10px]">
                        {waterInlets} inlet × ₹225
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold">
                        {billDetails.waterCharges.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 bg-orange-50/50">
                      <td className="py-2.5 px-2 font-medium text-orange-800">
                        Sinking Fund
                        <span className="block text-[9px] text-orange-600 font-normal">
                          Bye-law 66
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-orange-700 text-[10px]">
                        0.25% of ₹{(constructionCost / 100000).toFixed(1)}L
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold text-orange-800">
                        {billDetails.sinkingFund.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100 bg-blue-50/50">
                      <td className="py-2.5 px-2 font-medium text-blue-800">
                        Repair Fund
                        <span className="block text-[9px] text-blue-600 font-normal">
                          Bye-law 67
                        </span>
                      </td>
                      <td className="py-2.5 px-2 text-right text-blue-700 text-[10px]">
                        0.75% of ₹{(constructionCost / 100000).toFixed(1)}L
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold text-blue-800">
                        {billDetails.repairFund.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2.5 px-2 font-medium">
                        Service Charges
                      </td>
                      <td className="py-2.5 px-2 text-right text-gray-500 text-[10px]">
                        Equal/Flat
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold">
                        {billDetails.serviceCharges.toFixed(2)}
                      </td>
                    </tr>
                    <tr className="border-b border-gray-100">
                      <td className="py-2.5 px-2 font-medium">
                        Parking Charges
                      </td>
                      <td className="py-2.5 px-2 text-right text-gray-500 text-[10px]">
                        {cars}C + {bikes}B
                      </td>
                      <td className="py-2.5 px-2 text-right font-mono font-bold">
                        {billDetails.parkingCharges.toFixed(2)}
                      </td>
                    </tr>
                    {isRented && (
                      <tr className="border-b border-gray-100 bg-purple-50/50">
                        <td className="py-2.5 px-2 font-medium text-purple-800">
                          Non-Occupancy
                          <span className="block text-[9px] text-purple-600 font-normal">
                            Bye-law 42 (Max 10%)
                          </span>
                        </td>
                        <td className="py-2.5 px-2 text-right text-purple-700 text-[10px]">
                          10% of Service
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono font-bold text-purple-800">
                          {billDetails.nonOccupancy.toFixed(2)}
                        </td>
                      </tr>
                    )}
                    {!isRented && (
                      <tr className="border-b border-gray-100">
                        <td className="py-2.5 px-2 font-medium text-gray-400">
                          Non-Occupancy
                        </td>
                        <td className="py-2.5 px-2 text-right text-gray-400 text-[10px]">
                          Owner Occupied
                        </td>
                        <td className="py-2.5 px-2 text-right font-mono text-gray-400">
                          N/A
                        </td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-gray-800">
                      <td
                        colSpan={2}
                        className="text-right pt-3 pb-2 pr-2 text-xs font-bold uppercase tracking-wider"
                      >
                        Total Payable
                      </td>
                      <td className="text-right pt-3 pb-2 text-lg font-black text-gray-900">
                        ₹{billDetails.totalPayable.toLocaleString("en-IN")}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Footer */}
              <div className="flex gap-4 items-start border-t border-dashed border-gray-300 pt-4">
                <div className="p-1 border border-gray-200 rounded bg-white shadow-sm shrink-0">
                  <Image
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=upi://pay?pa=gokuldham@sbi&pn=Gokuldham&am=${billDetails.totalPayable}&tn=Maintenance`}
                    alt="UPI QR code for society maintenance bill payment via GPay, PhonePe, or Paytm"
                    width={60}
                    height={60}
                    className="w-14 h-14 opacity-90"
                    unoptimized={true}
                  />
                  <p className="text-[8px] text-center font-bold mt-0.5 text-gray-500">
                    SCAN TO PAY
                  </p>
                </div>
                <div className="flex-1 text-[10px] text-gray-600">
                  <p className="font-semibold text-gray-800 mb-1">
                    Payment Instructions
                  </p>
                  <ul className="space-y-0.5 list-disc pl-3">
                    <li>Scan QR using GPay/PhonePe/Paytm</li>
                    <li>NEFT: Acct 1234567890 (SBI)</li>
                    <li>Late fee: 21% p.a. simple interest</li>
                  </ul>
                </div>
                <div className="text-right shrink-0">
                  <div className="h-6 border-b border-gray-400 w-16 mb-0.5"></div>
                  <p className="text-[9px] font-bold text-gray-500 uppercase">
                    Hon. Secretary
                  </p>
                </div>
              </div>

              {/* Download Button */}
              <div className="absolute -top-3 -right-3 md:top-4 md:-right-16">
                <button className="bg-[#111] text-white p-3 rounded-full shadow-xl hover:scale-110 transition-transform">
                  <Download className="w-4 h-4" />
                </button>
              </div>

              {/* Watermark */}
              <div className="absolute bottom-2 left-0 w-full text-center">
                <p className="text-[8px] text-gray-400">
                  Generated by <strong>SmartDwara ERP</strong> • Form N
                  Compliant
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* --- WORKFLOW GRID --- */}
        <div className="workflow-grid grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          {[
            {
              icon: Zap,
              title: "1-Click Generate",
              desc: "Generate Form N bills for all flats instantly.",
            },
            {
              icon: Mail,
              title: "Auto-Email",
              desc: "PDFs emailed + WhatsApp notification.",
            },
            {
              icon: LinkIcon,
              title: "Payment Link",
              desc: "Razorpay/UPI link embedded in bill.",
            },
            {
              icon: RefreshCw,
              title: "Auto-Reconcile",
              desc: "Payments auto-mapped to ledgers.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="workflow-card bg-[#111] border border-white/5 p-6 rounded-2xl flex flex-col items-center text-center hover:bg-white/5 transition-colors group"
            >
              <div className="w-12 h-12 rounded-full bg-orange-500/10 flex items-center justify-center text-orange-500 mb-4 group-hover:scale-110 transition-transform">
                <item.icon className="w-6 h-6" />
              </div>
              <h4 className="text-white mb-2 font-clash font-semibold tracking-wide">
                {item.title}
              </h4>
              <p className="text-gray-500 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* --- CTA --- */}
        <div className="text-center">
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-8 py-4 bg-white text-black font-clash font-semibold tracking-wide rounded-full text-lg hover:bg-gray-200 transition-colors shadow-[0_0_30px_rgba(255,255,255,0.1)] active:scale-95 inline-flex items-center gap-2"
          >
            <BookOpen className="w-5 h-5" />
            See Your Society Breakdown →
          </button>
        </div>
      </div>

      {/* MODAL */}
      <BreakdownModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </section>
  );
};

export default BillingSection;
